import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, resolveImage, API } from "@/lib/api";
import { trackPixel } from "@/lib/pixel";
import EnquiryDialog from "@/components/EnquiryDialog";
import YachtCard from "@/components/YachtCard";
import Gallery from "@/components/yacht/Gallery";
import SpecSheet from "@/components/yacht/SpecSheet";
import ShareButton from "@/components/yacht/ShareButton";
import BrandTheme from "@/components/BrandTheme";

export default function YachtDetailPage() {
  const { slug, yachtSlug } = useParams();
  const [enquire, setEnquire] = useState(false);
  const { data: yacht, isLoading, isError } = useQuery({
    queryKey: ["yacht", slug, yachtSlug],
    queryFn: async () => (await api.get(`/brands/${slug}/yachts/${yachtSlug}`)).data,
  });

  useEffect(() => {
    if (!yacht) return;
    trackPixel("ViewContent", { content_name: `${yacht.brand.name} ${yacht.name}`, content_category: yacht.brand.name, content_type: "product" });
    const prev = document.title;
    document.title = `${yacht.brand.name} ${yacht.name} — Modi Marine`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta && yacht.tagline) meta.setAttribute("content", yacht.tagline);
    return () => {
      document.title = prev;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [yacht]);

  if (isLoading) return <div className="px-6 pt-40 text-sm text-mist lg:px-12" data-testid="yacht-page-loading">Loading…</div>;
  if (isError || !yacht) {
    return (
      <div className="px-6 pb-24 pt-40 lg:px-12" data-testid="yacht-page-not-found">
        <p className="font-serif text-3xl text-navy-ink">This model could not be found.</p>
        <Link to={`/yachts/${slug}`} className="mt-6 inline-block text-[11px] uppercase tracking-[0.3em] text-gold">Back to the range</Link>
      </div>
    );
  }

  const { brand } = yacht;
  const hero = yacht.gallery?.[0] || yacht.image;
  const gallery = yacht.gallery?.length ? yacht.gallery : [yacht.image];
  const shareUrl = `${API}/share/yachts/${brand.slug}/${yacht.slug}`;
  const shareProps = {
    shareUrl,
    title: `${brand.name} ${yacht.name} — Modi Marine`,
    text: `Have a look at the ${brand.name} ${yacht.name} from Modi Marine: ${yacht.tagline || ""}`.trim(),
  };
  const brochureHref = yacht.brochure_url && !yacht.brochure_url.startsWith("/api/files/")
    ? yacht.brochure_url
    : `${API}/yachts/${yacht.id}/brochure.pdf`;

  return (
    <BrandTheme theme={brand.theme} data-testid="yacht-detail-page">
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden" data-testid="yacht-hero">
        <img src={resolveImage(hero)} alt={yacht.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-navy to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-14 lg:px-12">
          <div className="mx-auto max-w-7xl">
            <nav className="mb-8 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-white/80" data-testid="yacht-breadcrumb">
              <Link to="/yachts" className="hover:text-gold-pale">New Yachts</Link>
              <span>/</span>
              <Link to={`/yachts/${brand.slug}`} className="hover:text-gold-pale" data-testid="yacht-breadcrumb-brand">{brand.name}</Link>
              <span>/</span>
              <span className="text-gold-pale">{yacht.name}</span>
            </nav>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-5 flex items-center gap-4">
              {brand.logo_light && <img src={resolveImage(brand.logo_light)} alt={brand.name} className="h-5 w-auto sm:h-6" data-testid="yacht-hero-brand-logo" />}
              <span className="text-[10px] uppercase tracking-[0.45em] text-gold-pale sm:text-xs">{brand.logo_light ? yacht.category : `${brand.name} · ${yacht.category}`}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="brand-heading font-serif text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl" data-testid="yacht-heading">
              {yacht.name}
            </motion.h1>
            {yacht.tagline && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.3 }} className="mt-6 max-w-xl font-serif text-xl italic text-white/85 sm:text-2xl" data-testid="yacht-tagline">
                {yacht.tagline}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-navy px-6 py-14 lg:px-12" data-testid="yacht-key-specs">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 border-b border-navy-ink/10 pb-14 lg:flex-row lg:items-center lg:justify-between">
          <div className="grid flex-1 grid-cols-2 gap-6 sm:grid-cols-4">
            {yacht.specs.map((s) => (
              <div key={s.label} className="border-l border-gold/40 pl-4" data-testid={`yacht-key-spec-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="text-[9px] uppercase tracking-[0.3em] text-mist">{s.label}</p>
                <p className="mt-2 font-serif text-2xl text-navy-ink">{s.value}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setEnquire(true)} data-testid="yacht-enquire-button" className="inline-flex items-center gap-2 border border-gold bg-gold px-7 py-4 text-[11px] uppercase tracking-[0.3em] text-navy transition-colors duration-300 hover:bg-transparent hover:text-gold">
              Enquire <ArrowUpRight size={14} />
            </button>
            <a href={brochureHref} target="_blank" rel="noreferrer" data-testid="yacht-brochure-button" className="inline-flex items-center gap-2 border border-navy-ink/20 px-7 py-4 text-[11px] uppercase tracking-[0.3em] text-navy-ink transition-colors duration-300 hover:border-gold hover:text-gold">
              <Download size={14} /> Brochure
            </a>
            <ShareButton {...shareProps} />
          </div>
        </div>
      </section>

      <section className="bg-navy px-6 pb-8 pt-10 lg:px-12 lg:pb-16" data-testid="yacht-story">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold">The {yacht.name}</p>
            <p className="brand-body brand-ink max-w-2xl font-serif text-2xl font-light leading-relaxed text-navy-ink sm:text-3xl" data-testid="yacht-description">
              {yacht.description}
            </p>
          </div>
          {yacht.highlights?.length > 0 && (
            <ul className="space-y-4 border-l border-navy-ink/10 pl-8 lg:col-span-5" data-testid="yacht-highlights">
              {yacht.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm font-light leading-relaxed text-mist">
                  <Check size={14} className="mt-1 shrink-0 text-gold" /> {h}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <Gallery images={gallery} name={yacht.name} />
      <SpecSheet groups={yacht.spec_sheet} />

      <section className="bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="yacht-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 border border-navy-ink/10 bg-navy-light/40 p-10 lg:flex-row lg:items-center lg:p-14">
          <div>
            <p className="text-[10px] uppercase tracking-[0.45em] text-gold">Private consultation</p>
            <h2 className="brand-heading brand-ink mt-3 font-serif text-3xl font-light text-navy-ink sm:text-4xl">Specify your {yacht.name}.</h2>
            <p className="mt-3 max-w-lg text-sm font-light text-mist">Layouts, finishes and delivery to any Indian marina — arranged personally by our private office.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setEnquire(true)} data-testid="yacht-cta-enquire-button" className="border border-gold bg-gold px-7 py-4 text-[11px] uppercase tracking-[0.3em] text-navy transition-colors duration-300 hover:bg-transparent hover:text-gold">Enquire now</button>
            <a href={brochureHref} target="_blank" rel="noreferrer" data-testid="yacht-cta-brochure-button" className="inline-flex items-center gap-2 border border-navy-ink/20 px-7 py-4 text-[11px] uppercase tracking-[0.3em] text-navy-ink transition-colors hover:border-gold hover:text-gold"><Download size={14} /> Download brochure</a>
            <ShareButton testId="yacht-cta-share" {...shareProps} />
          </div>
        </div>
      </section>

      {yacht.related?.length > 0 && (
        <section className="border-t border-navy-ink/5 bg-navy px-6 pb-24 pt-16 lg:px-12 lg:pb-36" data-testid="yacht-related">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 flex items-end justify-between">
              <h2 className="brand-heading brand-ink font-serif text-3xl font-light text-navy-ink sm:text-4xl">More from {brand.name}</h2>
              <Link to={`/yachts/${brand.slug}`} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold" data-testid="yacht-related-all">
                <ArrowLeft size={12} /> Full range
              </Link>
            </div>
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
              {yacht.related.map((y, i) => (
                <YachtCard key={y.id} yacht={y} index={i} span="lg:col-span-4" brandLabel={`${brand.name} · ${brand.origin}`} compact />
              ))}
            </div>
          </div>
        </section>
      )}

      <EnquiryDialog open={enquire} onClose={() => setEnquire(false)} yacht={yacht} brandName={brand.name} />
    </BrandTheme>
  );
}
